
import { useState } from "react";
import { PortfolioData } from "@/pages/DigitalPortfolio";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Save, 
  Share, 
  Settings, 
  Globe, 
  Lock, 
  Eye
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface PortfolioHeaderProps {
  portfolio: PortfolioData;
  updatePortfolio: (updates: Partial<PortfolioData>) => void;
  onSave: () => void;
}

export const PortfolioHeader = ({ 
  portfolio, 
  updatePortfolio,
  onSave
}: PortfolioHeaderProps) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);

  const visibilityOptions = [
    { value: "public", label: "Public", icon: Globe },
    { value: "private", label: "Private", icon: Lock },
    { value: "unlisted", label: "Unlisted", icon: Eye },
  ];

  const getVisibilityLabel = () => {
    return visibilityOptions.find(option => option.value === portfolio.visibility)?.label || "Private";
  };

  const getVisibilityIcon = () => {
    const Icon = visibilityOptions.find(option => option.value === portfolio.visibility)?.icon || Lock;
    return <Icon className="h-4 w-4 mr-2" />;
  };

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div className="flex-1">
        <Input
          className="text-xl font-bold border-none p-0 h-auto text-gray-900 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
          value={portfolio.title}
          onChange={(e) => updatePortfolio({ title: e.target.value })}
          placeholder="Portfolio Title"
        />
        <p className="text-gray-500 text-sm mt-1">
          {portfolio.items.length} items · Last edited just now
        </p>
      </div>

      <div className="flex items-center gap-2 mt-2 md:mt-0">
        {/* Visibility Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="flex items-center">
              {getVisibilityIcon()}
              {getVisibilityLabel()}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {visibilityOptions.map((option) => (
              <DropdownMenuItem 
                key={option.value}
                onClick={() => updatePortfolio({ visibility: option.value as any })}
                className="flex items-center cursor-pointer"
              >
                <option.icon className="h-4 w-4 mr-2" />
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Settings Dialog */}
        <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Portfolio Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Portfolio Title</label>
                <Input 
                  value={portfolio.title} 
                  onChange={(e) => updatePortfolio({ title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Input 
                  value={portfolio.description} 
                  onChange={(e) => updatePortfolio({ description: e.target.value })}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Share Dialog */}
        <Dialog open={isShareOpen} onOpenChange={setIsShareOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Share className="h-4 w-4 mr-2" />
              Share
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Share Portfolio</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-500">
                Share your portfolio with others using the link below:
              </p>
              <div className="flex items-center gap-2">
                <Input 
                  value={`https://dgkhub.com/portfolio/${portfolio.id}`} 
                  readOnly 
                  className="flex-1"
                />
                <Button size="sm" onClick={() => {
                  navigator.clipboard.writeText(`https://dgkhub.com/portfolio/${portfolio.id}`);
                }}>
                  Copy
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Save Button */}
        <Button onClick={onSave}>
          <Save className="h-4 w-4 mr-2" />
          Save
        </Button>
      </div>
    </div>
  );
};
